import React, { useState, useRef } from 'react';
import axios from 'axios';
import useScreenSize from '../../hooks/useScreenSize';

const VideoTranscriber = ({ isOpen, onClose, onTranscriptionComplete }) => {
  const { isMobile } = useScreenSize();
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size
      if (file.size > MAX_VIDEO_SIZE) {
        setError('Video file is too large. Maximum size is 500MB.');
        return;
      }

      setVideoFile(file);
      setYoutubeUrl(''); // Clear YouTube URL if file is selected
      setError(null);
    }
  };

  const handleYoutubeUrlChange = (e) => {
    setYoutubeUrl(e.target.value);
    if (e.target.value) {
      setVideoFile(null); // Clear file if URL is entered
    }
    setError(null);
  };

  const handleSubmit = async () => {
    if (!youtubeUrl && !videoFile) {
      setError('Please provide either a YouTube URL or upload a video file');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResult(null);
    setProgress('Preparing...');

    try {
      // Create FormData
      const formData = new FormData();

      if (youtubeUrl) {
        formData.append('youtube_url', youtubeUrl);
        setProgress('Downloading YouTube audio...');
      } else if (videoFile) {
        formData.append('video_file', videoFile);
        setProgress('Uploading video file...');
      }

      formData.append('language', 'en');
      formData.append('enhance', 'true');

      // Send to backend
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      setProgress('Processing video...');

      const response = await axios.post(`${API_URL}/ai/transcribe-video`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true,
        timeout: 600000, // 10 minutes timeout for longer videos
        onUploadProgress: (progressEvent) => {
          if (videoFile) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(`Uploading video... ${percentCompleted}%`);
          }
        }
      });

      if (response.data.success) {
        setResult(response.data);
        setProgress('Complete!');

        // Format the content for insertion
        const formattedContent = formatTranscriptionResult(response.data);

        // Call parent callback with formatted content
        if (onTranscriptionComplete) {
          onTranscriptionComplete(formattedContent);
        }
      } else {
        setError(response.data.error || 'Transcription failed');
      }
    } catch (err) {
      console.error('Video transcription error:', err);

      if (err.code === 'ECONNABORTED') {
        setError('Processing timed out. Video may be too long. Try a shorter video.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to process video. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTranscriptionResult = (data) => {
    let content = '';

    // Title
    if (data.title) {
      content += `<h1>${data.title}</h1>\n\n`;
    }

    // Summary
    if (data.summary) {
      content += `<h2>Summary</h2>\n`;
      content += `<p>${data.summary}</p>\n\n`;
    }

    // Key Points
    if (data.key_points && data.key_points.length > 0) {
      content += `<h2>Key Points</h2>\n`;
      content += `<ul>\n`;
      data.key_points.forEach(point => {
        content += `<li>${point}</li>\n`;
      });
      content += `</ul>\n\n`;
    }

    // Topics
    if (data.topics && data.topics.length > 0) {
      content += `<h2>Topics Covered</h2>\n`;
      content += `<ul>\n`;
      data.topics.forEach(topic => {
        content += `<li>${topic}</li>\n`;
      });
      content += `</ul>\n\n`;
    }

    // Full Transcript
    if (data.transcript) {
      content += `<h2>Full Transcript</h2>\n`;
      content += `<p>${data.transcript}</p>\n`;
    }

    return content;
  };

  const handleInsert = () => {
    if (result) {
      const formattedContent = formatTranscriptionResult(result);
      if (onTranscriptionComplete) {
        onTranscriptionComplete(formattedContent);
      }
      handleClose();
    }
  };

  const handleClose = () => {
    setYoutubeUrl('');
    setVideoFile(null);
    setIsProcessing(false);
    setProgress('');
    setError(null);
    setResult(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={(e) => {
      if (e.target === e.currentTarget && !isProcessing) {
        handleClose();
      }
    }}>
      <div
        style={{
          ...styles.modal,
          width: isMobile ? '95%' : '90%',
          maxWidth: isMobile ? 'none' : '600px',
          padding: isMobile ? '24px' : '32px',
        }}
      >
        <h2 style={styles.title}>🎬 Video Transcription</h2>

        {!result && (
          <>
            {/* YouTube URL Input */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>YouTube URL</label>
              <input
                type="text"
                value={youtubeUrl}
                onChange={handleYoutubeUrlChange}
                placeholder="https://www.youtube.com/watch?v=..."
                style={styles.input}
                disabled={isProcessing || videoFile !== null}
              />
            </div>

            {/* OR Divider */}
            <div style={styles.divider}>
              <span style={styles.dividerText}>OR</span>
            </div>

            {/* Video File Upload */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Upload Video File</label>
              <div style={styles.fileInputWrapper}>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="internal-secondary-action"
                  style={{
                    ...styles.button,
                    ...styles.fileButton,
                    minHeight: isMobile ? '48px' : 'auto',
                    padding: isMobile ? '14px 24px' : '14px 28px',
                  }}
                  disabled={isProcessing || youtubeUrl !== ''}
                >
                  {videoFile ? videoFile.name : 'Choose Video File'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/mov,video/avi,video/mkv,.mp4,.webm,.mov,.avi,.mkv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>
              {videoFile && (
                <div style={styles.fileInfo}>
                  Selected: {videoFile.name} ({(videoFile.size / (1024 * 1024)).toFixed(1)} MB)
                </div>
              )}
              <div style={styles.hint}>
                Supported formats: MP4, WebM, MOV, AVI, MKV (max 500MB)
              </div>
            </div>

            {/* Processing Status */}
            {isProcessing && (
              <div style={styles.processingBox}>
                <div style={styles.spinner} />
                <div style={styles.progressText}>{progress}</div>
                <div style={styles.hint}>
                  This may take a few minutes for longer videos...
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div style={styles.errorBox}>
                {error}
              </div>
            )}

            {/* Submit Button */}
            {!isProcessing && (
              <div style={styles.controls}>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="internal-primary-action"
                  style={{
                    ...styles.button,
                    ...styles.submitButton,
                    padding: isMobile ? '14px 24px' : '14px 28px',
                    minHeight: isMobile ? '48px' : 'auto',
                  }}
                  disabled={!youtubeUrl && !videoFile}
                >
                  Transcribe Video
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="internal-secondary-action"
                  style={{
                    ...styles.button,
                    ...styles.cancelButton,
                    padding: isMobile ? '14px 24px' : '14px 28px',
                    minHeight: isMobile ? '48px' : 'auto',
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}

        {/* Results Display */}
        {result && !isProcessing && (
          <div style={styles.resultBox}>
            <div style={styles.successMessage}>
              ✓ Transcription Complete!
            </div>

            {/* Title */}
            {result.title && (
              <div style={styles.resultSection}>
                <h3 style={styles.resultTitle}>Video Title</h3>
                <p style={styles.resultText}>{result.title}</p>
              </div>
            )}

            {/* Summary */}
            {result.summary && (
              <div style={styles.resultSection}>
                <h3 style={styles.resultTitle}>Summary</h3>
                <p style={styles.resultText}>{result.summary}</p>
              </div>
            )}

            {/* Key Points */}
            {result.key_points && result.key_points.length > 0 && (
              <div style={styles.resultSection}>
                <h3 style={styles.resultTitle}>Key Points</h3>
                <ul style={styles.list}>
                  {result.key_points.map((point, index) => (
                    <li key={index} style={styles.listItem}>{point}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Topics */}
            {result.topics && result.topics.length > 0 && (
              <div style={styles.resultSection}>
                <h3 style={styles.resultTitle}>Topics Covered</h3>
                <ul style={styles.list}>
                  {result.topics.map((topic, index) => (
                    <li key={index} style={styles.listItem}>{topic}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Transcript Preview */}
            {result.transcript && (
              <div style={styles.resultSection}>
                <h3 style={styles.resultTitle}>Full Transcript</h3>
                <div style={styles.transcriptPreview}>
                  {result.transcript.substring(0, 300)}
                  {result.transcript.length > 300 && '...'}
                </div>
                <div style={styles.hint}>
                  Full transcript will be inserted into your note
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={styles.controls}>
              <button
                type="button"
                onClick={handleInsert}
                className="success-action"
                style={{
                  ...styles.button,
                  ...styles.insertButton,
                  padding: isMobile ? '14px 24px' : '14px 28px',
                  minHeight: isMobile ? '48px' : 'auto',
                }}
              >
                Insert Into Note
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="internal-secondary-action"
                style={{
                  ...styles.button,
                  ...styles.cancelButton,
                  padding: isMobile ? '14px 24px' : '14px 28px',
                  minHeight: isMobile ? '48px' : 'auto',
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Info Text */}
        {!isProcessing && !result && (
          <div style={styles.infoText}>
            💡 Transcribe YouTube videos or upload your own video files. AI will generate a summary and extract key points.
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  modal: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
  },
  title: {
    margin: '0 0 24px 0',
    fontSize: '24px',
    fontWeight: '600',
    textAlign: 'center',
    background: '#0778ad',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
  },
  dividerText: {
    margin: '0 auto',
    padding: '0 16px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#999',
    position: 'relative',
    '::before': {
      content: '""',
      position: 'absolute',
      left: '-100px',
      top: '50%',
      width: '100px',
      height: '1px',
      background: '#ddd',
    },
    '::after': {
      content: '""',
      position: 'absolute',
      right: '-100px',
      top: '50%',
      width: '100px',
      height: '1px',
      background: '#ddd',
    }
  },
  fileInputWrapper: {
    marginBottom: '8px',
  },
  fileInfo: {
    fontSize: '13px',
    color: '#666',
    marginTop: '8px',
  },
  hint: {
    fontSize: '12px',
    color: '#999',
    marginTop: '4px',
  },
  processingBox: {
    textAlign: 'center',
    padding: '24px',
    background: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #0778ad',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px',
  },
  progressText: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#0778ad',
    marginBottom: '8px',
  },
  errorBox: {
    background: '#fff0f0',
    border: '1px solid #ffcccc',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    color: '#d63031',
    fontSize: '14px',
  },
  controls: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    marginTop: '16px',
  },
  button: {
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flex: 1,
    maxWidth: '200px',
  },
  submitButton: {
    background: '#0778ad',
    color: 'white',
    boxShadow: '0 4px 12px rgba(8, 126, 184, 0.25)',
  },
  insertButton: {
    background: '#067647',
    color: 'white',
    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
  },
  fileButton: {
    background: '#f0f0f0',
    color: '#333',
    border: '1px solid #c7dce8',
    width: '100%',
    maxWidth: 'none',
  },
  cancelButton: {
    background: '#f0f0f0',
    color: '#334e68',
    border: '1px solid #c7dce8',
  },
  infoText: {
    marginTop: '16px',
    padding: '12px',
    background: '#f0f8ff',
    border: '1px solid #b3d9ff',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#0066cc',
    textAlign: 'center',
  },
  resultBox: {
    marginTop: '16px',
  },
  successMessage: {
    textAlign: 'center',
    fontSize: '18px',
    fontWeight: '600',
    color: '#067647',
    marginBottom: '24px',
  },
  resultSection: {
    marginBottom: '20px',
    padding: '16px',
    background: '#f8f9fa',
    borderRadius: '8px',
  },
  resultTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px',
  },
  resultText: {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.6',
    margin: '0',
  },
  list: {
    margin: '0',
    paddingLeft: '20px',
  },
  listItem: {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '4px',
  },
  transcriptPreview: {
    fontSize: '13px',
    color: '#666',
    lineHeight: '1.6',
    padding: '12px',
    background: 'white',
    borderRadius: '6px',
    maxHeight: '150px',
    overflowY: 'auto',
  },
};

// Add CSS animation for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default VideoTranscriber;
